import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {
    FormulaInputTokenType,
    FormulaToken,
    ICellReferenceExternalFormulaToken,
    ICellReferenceFormulaToken,
    ICellReferenceRangeExternalFormulaToken,
    ICellReferenceRangeFormulaToken,
    IFormulaToken,
    ILinearFormulaToken
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {
    R1C1Location,
    R1C1LocationIndexes,
    R1C1Range
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/R1C1';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';

export class FormulaInputController
{
    public cellReferenceErrorText:string = '#REF!';

    public getPlainFormulaTokens(formulaToken:FormulaToken):Array<ILinearFormulaToken>
    {
        const plainTokens:Array<ILinearFormulaToken> = [];

        if( formulaToken instanceof Array )
            formulaToken.map((token:IFormulaToken) => this._processNextToken(plainTokens, token));
        else if( formulaToken )
            this._processNextToken(plainTokens, formulaToken);

        this._processCellRefsMatches(plainTokens);

        return plainTokens;
    }

    private _processNextToken(plainTokens:Array<ILinearFormulaToken>,
                              nextToken:IFormulaToken,
                              currentFunctionTokenIndex:number = null,
                              functionArgumentIndex:number = null,
                              matchTokenIndexes:Array<number> = []):ILinearFormulaToken
    {
        if( !nextToken )
            return null;

        let token:ILinearFormulaToken = null;

        switch( nextToken.type )
        {
            case FormulaInputTokenType.FunctionCall:
                this._processFunctionCall(plainTokens, nextToken, functionArgumentIndex);
                break;
            case FormulaInputTokenType.FunctionArgument:
                this._processFunctionArgument(plainTokens, nextToken, currentFunctionTokenIndex, functionArgumentIndex);
                break;
            case FormulaInputTokenType.Parentheses:
                this._processParentheses(plainTokens, nextToken, currentFunctionTokenIndex, functionArgumentIndex);
                break;
            case FormulaInputTokenType.QualifiedFunctionName: // NOTE: Don't change order of the QualifiedFunctionName, SimpleFunctionName and default cases
            case FormulaInputTokenType.SimpleFunctionName:
                currentFunctionTokenIndex = plainTokens.length;
            default:
                token = {
                    index: plainTokens.length,
                    token: nextToken,
                    functionTokenIndex: currentFunctionTokenIndex,
                    functionArgumentIndex,
                    matchTokenIndexes
                };
                plainTokens.push(token);
                break;
        }

        return token;
    }

    private _processFunctionCall(plainTokens:Array<ILinearFormulaToken>,
                                 nextToken:IFormulaToken,
                                 functionArgumentIndex:number):void
    {
        const currentFunctionToken:ILinearFormulaToken = this._processNextToken(
            plainTokens,
            (nextToken.valueParts as any).functionName,
            plainTokens.length
        );

        this._processNextToken(
            plainTokens,
            (nextToken.valueParts as any).space,
            currentFunctionToken.index
        );

        const funcParenthesesStartToken:ILinearFormulaToken = this._processNextToken(
            plainTokens,
            (nextToken.valueParts as any).parenthesesStart,
            currentFunctionToken.index,
            functionArgumentIndex
        );

        const functionArguments:Array<IFormulaToken> = (nextToken.valueParts as any).functionArgument;

        if( functionArguments && functionArguments.length > 0 )
            functionArguments.map((token:IFormulaToken, index:number) => this._processNextToken(
                plainTokens,
                token,
                currentFunctionToken.index,
                index
            ));

        const funcParenthesesEndToken:ILinearFormulaToken = this._processNextToken(
            plainTokens,
            (nextToken.valueParts as any).parenthesesEnd,
            currentFunctionToken.index,
            null,
            (funcParenthesesStartToken) ? [funcParenthesesStartToken.index] : null);

        if( funcParenthesesEndToken )
            funcParenthesesStartToken.matchTokenIndexes = [funcParenthesesEndToken.index];
    }

    private _processFunctionArgument(plainTokens:Array<ILinearFormulaToken>,
                                     nextToken:IFormulaToken,
                                     currentFunctionTokenIndex:number,
                                     functionArgumentIndex:number):void
    {
        const argumentContent:Array<IFormulaToken> = (nextToken.valueParts as any).content;

        if( argumentContent && argumentContent.length > 0 )
            argumentContent.map((token:IFormulaToken) => this._processNextToken(
                plainTokens,
                token,
                currentFunctionTokenIndex,
                functionArgumentIndex
            ));

        this._processNextToken(
            plainTokens,
            (nextToken.valueParts as any).comma,
            currentFunctionTokenIndex,
            functionArgumentIndex + 1
        );
    }

    private _processParentheses(plainTokens:Array<ILinearFormulaToken>,
                                nextToken:IFormulaToken,
                                currentFunctionTokenIndex:number,
                                functionArgumentIndex:number):void
    {
        const parenthesesStart:ILinearFormulaToken = this._processNextToken(
            plainTokens,
            (nextToken.valueParts as any).start,
            currentFunctionTokenIndex,
            functionArgumentIndex
        );

        const content:Array<IFormulaToken> = (nextToken.valueParts as any).content;

        if( content && content.length > 0 )
            content.map((token:IFormulaToken) => this._processNextToken(
                plainTokens,
                token,
                null,
                null
            ));

        const parenthesesEnd:ILinearFormulaToken = this._processNextToken(
            plainTokens,
            (nextToken.valueParts as any).end,
            null,
            null,
            [parenthesesStart.index]
        );

        if( parenthesesEnd )
            parenthesesStart.matchTokenIndexes = [parenthesesEnd.index];
    }

    private _processCellRefsMatches(plainTokens):void
    {
        for( const token of plainTokens )
        {
            if( token.token.type === FormulaInputTokenType.CellReference )
            {
                const location:R1C1Location = (token.token as ICellReferenceFormulaToken).location;

                for( const matchToken of plainTokens )
                    if( matchToken !== token )
                        if( matchToken.token.type === FormulaInputTokenType.CellReference )
                        {
                            const matchLocation:R1C1Location = (matchToken.token as ICellReferenceFormulaToken).location;

                            if( matchLocation.isEqualTo(location) )
                                token.matchTokenIndexes.push(matchToken.index);
                        }
            }
            else if( token.token.type === FormulaInputTokenType.CellReferenceRange )
            {
                const range:R1C1Range = (token.token as ICellReferenceRangeFormulaToken).range;

                for( const matchToken of plainTokens )
                    if( matchToken !== token )
                        if( matchToken.token.type === FormulaInputTokenType.CellReferenceRange )
                        {
                            const matchRange:R1C1Range = (matchToken.token as ICellReferenceRangeFormulaToken).range;

                            if( matchRange.isEqualTo(range) )
                                token.matchTokenIndexes.push(matchToken.index);
                        }
            }
        }
    }

    public getFormulaTokensText(formulaTokens:Array<ILinearFormulaToken>,
                                currentColumnIndex:number,
                                currentRowIndex:number,
                                isA1:boolean = false):string
    {
        if( formulaTokens )
            return formulaTokens.map(
                (formulaToken:ILinearFormulaToken) => this._getTokenValue(
                    formulaToken,
                    currentColumnIndex,
                    currentRowIndex,
                    isA1
                )
            ).join('');

        return '';
    }

    private _getTokenValue(formulaToken:ILinearFormulaToken,
                           currentColumnIndex:number = 0,
                           currentRowIndex:number = 0,
                           isA1:boolean = false):string
    {
        switch( formulaToken.token.type )
        {
            case FormulaInputTokenType.CellReference:
                const location:R1C1Location = (formulaToken.token as ICellReferenceFormulaToken).location;
                return isA1 ? location.toA1String(currentColumnIndex, currentRowIndex) : location.toString();
            case FormulaInputTokenType.CellReferenceRange:
                const range:R1C1Range = (formulaToken.token as ICellReferenceRangeFormulaToken).range;
                return isA1 ? range.toA1String(currentColumnIndex, currentRowIndex) : range.toString();
            case FormulaInputTokenType.CellReferenceExternal:
                const extLocation:R1C1Location = (formulaToken.token as ICellReferenceExternalFormulaToken).location;

                const sheetName:string = (formulaToken.token as ICellReferenceExternalFormulaToken).sheetName;
                const locationText = isA1 ? extLocation.toA1String(currentColumnIndex, currentRowIndex) : extLocation.toString();

                return `${sheetName}!${locationText}`;
            case FormulaInputTokenType.CellReferenceRangeExternal:
                const {startLocation, endLocation}:R1C1Range = (formulaToken.token as ICellReferenceRangeExternalFormulaToken).range;

                const sheetNameRange:string = (formulaToken.token as ICellReferenceRangeExternalFormulaToken).sheetName;
                const startLocationText = isA1 ? startLocation.toA1String(currentColumnIndex, currentRowIndex) : startLocation.toString();
                const endLocationText = endLocation ? (isA1 ? endLocation.toA1String(currentColumnIndex, currentRowIndex) : endLocation.toString()) : '';

                return `${sheetNameRange}!${startLocationText}:${endLocationText}`;
            default:
                return formulaToken.token.value;
        }
    }

    private _recalculateR1C1Location(location:R1C1Location,
                                     currentColumnIndex:number,
                                     currentRowIndex:number,
                                     fromColumnIndex:number,
                                     fromRowIndex:number,
                                     columnsCount:number,
                                     rowsCount:number):R1C1Location | null
    {
        let newColumn:number = location.columnOffset;
        let newRow:number = location.rowOffset;

        const {colIndex, rowIndex}:R1C1LocationIndexes = location.getIndexes(currentColumnIndex, currentRowIndex);

        if( location.columnOffset !== null )
        {
            if( columnsCount < 0 ) // Delete
            {
                if( colIndex >= fromColumnIndex && colIndex < fromColumnIndex - columnsCount ) // Ref to deleted column
                    return null;

                if( location.isColumnFixed )
                {
                    if(
                        (colIndex >= fromColumnIndex - columnsCount && currentColumnIndex >= fromColumnIndex - columnsCount) ||
                        (colIndex >= fromColumnIndex - columnsCount && currentColumnIndex < fromColumnIndex)
                    )
                        newColumn = location.columnOffset + columnsCount;
                }
                else
                {
                    if( colIndex < fromColumnIndex && currentColumnIndex > fromColumnIndex )
                        newColumn = location.columnOffset - columnsCount;
                    else if( colIndex >= fromColumnIndex - columnsCount && currentColumnIndex < fromColumnIndex )
                        newColumn = location.columnOffset + columnsCount;
                }
            }
            else if( columnsCount > 0 ) // Insert
            {
                if( location.isColumnFixed )
                {
                    if(
                        (colIndex >= fromColumnIndex && currentColumnIndex >= fromColumnIndex) ||
                        (colIndex >= fromColumnIndex && currentColumnIndex < fromColumnIndex)
                    )
                        newColumn = location.columnOffset + columnsCount;
                }
                else
                {
                    if( colIndex < fromColumnIndex && currentColumnIndex >= fromColumnIndex )
                        newColumn = location.columnOffset - columnsCount;
                    else if( colIndex >= fromColumnIndex && currentColumnIndex < fromColumnIndex )
                        newColumn = location.columnOffset + columnsCount;
                }
            }
        }

        if( location.rowOffset !== null )
        {
            if( rowsCount < 0 ) // Delete
            {
                if( rowIndex >= fromRowIndex && rowIndex < fromRowIndex - rowsCount ) // Ref to deleted row
                    return null;

                if( location.isRowFixed )
                {
                    if(
                        (rowIndex >= fromRowIndex - rowsCount && currentRowIndex >= fromRowIndex - rowsCount) ||
                        (rowIndex >= fromRowIndex - rowsCount && currentRowIndex < fromRowIndex)
                    )
                        newRow = location.rowOffset + rowsCount;
                }
                else
                {
                    if( rowIndex < fromRowIndex && currentRowIndex > fromRowIndex )
                        newRow = location.rowOffset - rowsCount;
                    else if( rowIndex >= fromRowIndex - rowsCount && currentRowIndex < fromRowIndex )
                        newRow = location.rowOffset + rowsCount;
                }
            }
            else if( rowsCount > 0 ) // Insert
            {
                if( location.isRowFixed )
                {
                    if(
                        (rowIndex >= fromRowIndex && currentRowIndex >= fromRowIndex) ||
                        (rowIndex >= fromRowIndex && currentRowIndex < fromRowIndex)
                    )
                        newRow = location.rowOffset + rowsCount;
                }
                else
                {
                    if( rowIndex < fromRowIndex && currentRowIndex >= fromRowIndex )
                        newRow = location.rowOffset - rowsCount;
                    else if( rowIndex >= fromRowIndex && currentRowIndex < fromRowIndex )
                        newRow = location.rowOffset + rowsCount;
                }
            }
        }

        return new R1C1Location(
            newRow,
            location.isRowFixed,
            newColumn,
            location.isColumnFixed
        );
    }

    private _recalculateExternalR1C1Location(location:R1C1Location,
                                             currentColumnIndex:number,
                                             currentRowIndex:number,
                                             fromColumnIndex:number,
                                             fromRowIndex:number,
                                             columnsCount:number,
                                             rowsCount:number,
                                             isChangeInExternalSheet:boolean = false):R1C1Location | null
    {
        let newColumnOffset:number = location.columnOffset;
        let newRowOffset:number = location.rowOffset;

        if( isChangeInExternalSheet )
        {
            const {colIndex, rowIndex}:R1C1LocationIndexes = location.getIndexes(currentColumnIndex, currentRowIndex);

            if( columnsCount !== 0 && location.columnOffset !== null ) // Delete/Insert
            {
                if(
                    columnsCount < 0 &&
                    colIndex >= fromColumnIndex &&
                    colIndex < fromColumnIndex - columnsCount
                ) // Ref to deleted column
                    return null;

                if( colIndex >= fromColumnIndex )
                    newColumnOffset = location.columnOffset + columnsCount;
            }

            if( rowsCount !== 0 && location.rowOffset !== null ) // Delete/Insert
            {
                if(
                    rowsCount < 0 &&
                    rowIndex >= fromRowIndex &&
                    rowIndex < fromRowIndex - rowsCount
                ) // Ref to deleted row
                    return null;

                if( rowIndex >= fromRowIndex )
                    newRowOffset = location.rowOffset + rowsCount;
            }
        }
        else
        {
            if( columnsCount !== 0 && !location.isColumnFixed && location.columnOffset !== null ) // Delete/Insert
                if( currentColumnIndex >= fromColumnIndex )
                    newColumnOffset = location.columnOffset - columnsCount;

            if( rowsCount !== 0 && !location.isRowFixed && location.rowOffset !== null ) // Delete/Insert
                if( currentRowIndex >= fromRowIndex )
                    newRowOffset = location.rowOffset - rowsCount;
        }

        return new R1C1Location(
            newRowOffset,
            location.isRowFixed,
            newColumnOffset,
            location.isColumnFixed
        );
    }

    private _recalculateR1C1Range(startLocation:R1C1Location,
                                  endLocation:R1C1Location,
                                  currentColumnIndex:number,
                                  currentRowIndex:number,
                                  fromColumnIndex:number,
                                  fromRowIndex:number,
                                  columnsCount:number,
                                  rowsCount:number,
                                  isRangeExternal:boolean,
                                  isChangeInExternalSheet:boolean):R1C1Range | null
    {
        if( isRangeExternal && !isChangeInExternalSheet )
        {
            return new R1C1Range(
                this._recalculateExternalR1C1Location(
                    startLocation,
                    currentColumnIndex,
                    currentRowIndex,
                    fromColumnIndex,
                    fromRowIndex,
                    columnsCount,
                    rowsCount,
                    false
                ),
                this._recalculateExternalR1C1Location(
                    endLocation,
                    currentColumnIndex,
                    currentRowIndex,
                    fromColumnIndex,
                    fromRowIndex,
                    columnsCount,
                    rowsCount,
                    false
                )
            );
        }

        const {colIndex: startColumnIndex, rowIndex: startRowIndex}:R1C1LocationIndexes = startLocation.getIndexes(currentColumnIndex, currentRowIndex);
        const {colIndex: endColumnIndex, rowIndex: endRowIndex}:R1C1LocationIndexes = endLocation.getIndexes(currentColumnIndex, currentRowIndex);

        let newStartColumnOffset:number = startLocation.columnOffset;
        let newStartRowOffset:number = startLocation.rowOffset;
        let newEndColumnOffset:number = endLocation.columnOffset;
        let newEndRowOffset:number = endLocation.rowOffset;

        if(
            (startLocation.columnOffset !== null && endLocation.columnOffset !== null && columnsCount < 0
                && startColumnIndex >= fromColumnIndex && endColumnIndex < fromColumnIndex - columnsCount) ||
            (startLocation.rowOffset !== null && endLocation.rowOffset !== null && rowsCount < 0
                && startRowIndex >= fromRowIndex && endRowIndex < fromRowIndex - rowsCount)
        ) // All range was deleted
            return null;

        if( columnsCount < 0 )
        {
            if( startLocation.columnOffset !== null && startColumnIndex >= fromColumnIndex && startColumnIndex < fromColumnIndex - columnsCount ) // only startLocation in deleted
                newStartColumnOffset += (fromColumnIndex - columnsCount) - startColumnIndex;
            else if( endLocation.columnOffset !== null && endColumnIndex >= fromColumnIndex && endColumnIndex < fromColumnIndex - columnsCount ) // only endLocation in deleted
                newEndColumnOffset += (fromColumnIndex - 1) - endColumnIndex;
        }
        else if( rowsCount < 0 )
        {
            if( startLocation.rowOffset !== null && startRowIndex >= fromRowIndex && startRowIndex < fromRowIndex - rowsCount ) // only startLocation in deleted
                newStartRowOffset += (fromRowIndex - rowsCount) - startRowIndex;
            else if( endLocation.rowOffset !== null && endRowIndex >= fromRowIndex && endRowIndex < fromRowIndex - rowsCount ) // only endLocation in deleted
                newEndRowOffset += (fromRowIndex - 1) - endRowIndex;
        }

        if( isChangeInExternalSheet )
        {
            return new R1C1Range(
                this._recalculateExternalR1C1Location(
                    new R1C1Location(
                        newStartRowOffset,
                        startLocation.isRowFixed,
                        newStartColumnOffset,
                        startLocation.isColumnFixed
                    ),
                    currentColumnIndex,
                    currentRowIndex,
                    fromColumnIndex,
                    fromRowIndex,
                    columnsCount,
                    rowsCount,
                    true
                ),
                this._recalculateExternalR1C1Location(
                    new R1C1Location(
                        newEndRowOffset,
                        endLocation.isRowFixed,
                        newEndColumnOffset,
                        endLocation.isColumnFixed
                    ),
                    currentColumnIndex,
                    currentRowIndex,
                    fromColumnIndex,
                    fromRowIndex,
                    columnsCount,
                    rowsCount,
                    true
                )
            );
        }

        return new R1C1Range(
            this._recalculateR1C1Location(
                new R1C1Location(
                    newStartRowOffset,
                    startLocation.isRowFixed,
                    newStartColumnOffset,
                    startLocation.isColumnFixed
                ),
                currentColumnIndex,
                currentRowIndex,
                fromColumnIndex,
                fromRowIndex,
                columnsCount,
                rowsCount
            ),
            this._recalculateR1C1Location(
                new R1C1Location(
                    newEndRowOffset,
                    endLocation.isRowFixed,
                    newEndColumnOffset,
                    endLocation.isColumnFixed
                ),
                currentColumnIndex,
                currentRowIndex,
                fromColumnIndex,
                fromRowIndex,
                columnsCount,
                rowsCount
            )
        );
    }

    private _getCellReferenceErrorToken(formulaToken:ILinearFormulaToken, tokenPositionOffset:number = 0):ILinearFormulaToken
    {
        const startIndex:number = formulaToken.token.startIndex + tokenPositionOffset;
        const endIndex:number = startIndex + this.cellReferenceErrorText.length;

        return {
            ...formulaToken,
            token: {
                type: FormulaInputTokenType.CellReferenceError,
                value: this.cellReferenceErrorText,
                startIndex,
                endIndex
            },
            matchTokenIndexes: []
        };
    }

    public recalculateCellReferencesForMovingCells(formulaTokens:Array<ILinearFormulaToken>,
                                                   currentColumnIndex:number,
                                                   currentRowIndex:number,
                                                   fromColumnIndex:number,
                                                   fromRowIndex:number,
                                                   columnsCount:number,
                                                   rowsCount:number,
                                                   isChangeInExternalSheet:boolean = false):Array<ILinearFormulaToken>
    {
        let tokenPositionOffset = 0;

        return formulaTokens && formulaTokens.map((formulaToken:ILinearFormulaToken):ILinearFormulaToken =>
        {
            if( formulaToken.token.type === FormulaInputTokenType.CellReference
                || formulaToken.token.type === FormulaInputTokenType.CellReferenceExternal )
            {
                const location:R1C1Location = (formulaToken.token as ICellReferenceFormulaToken).location;

                const newLocation:R1C1Location = formulaToken.token.type === FormulaInputTokenType.CellReference
                    ? this._recalculateR1C1Location(
                        location,
                        currentColumnIndex,
                        currentRowIndex,
                        fromColumnIndex,
                        fromRowIndex,
                        columnsCount,
                        rowsCount
                    )
                    : this._recalculateExternalR1C1Location(
                        location,
                        currentColumnIndex,
                        currentRowIndex,
                        fromColumnIndex,
                        fromRowIndex,
                        columnsCount,
                        rowsCount,
                        isChangeInExternalSheet
                    );

                if( !newLocation )
                {
                    const cellRefErrorToken:ILinearFormulaToken = this._getCellReferenceErrorToken(formulaToken, tokenPositionOffset);

                    tokenPositionOffset -= formulaToken.token.endIndex - cellRefErrorToken.token.endIndex;

                    return cellRefErrorToken;
                }
                else
                    return {
                        ...formulaToken,
                        token: {
                            ...formulaToken.token,
                            location: newLocation,
                            range: new R1C1Range(newLocation, newLocation),
                            startIndex: formulaToken.token.startIndex + tokenPositionOffset,
                            endIndex: formulaToken.token.endIndex + tokenPositionOffset
                        } as IFormulaToken
                    } as ILinearFormulaToken;
            }
            else if( formulaToken.token.type === FormulaInputTokenType.CellReferenceRange
                || formulaToken.token.type === FormulaInputTokenType.CellReferenceRangeExternal )
            {
                const {startLocation, endLocation} = (formulaToken.token as ICellReferenceRangeFormulaToken).range;

                let newRange:R1C1Range | null = this._recalculateR1C1Range(
                    startLocation,
                    endLocation,
                    currentColumnIndex,
                    currentRowIndex,
                    fromColumnIndex,
                    fromRowIndex,
                    columnsCount,
                    rowsCount,
                    formulaToken.token.type === FormulaInputTokenType.CellReferenceRangeExternal,
                    isChangeInExternalSheet
                );

                if( !newRange )
                {
                    const cellRefErrorToken:ILinearFormulaToken = this._getCellReferenceErrorToken(formulaToken, tokenPositionOffset);

                    tokenPositionOffset -= formulaToken.token.endIndex - cellRefErrorToken.token.endIndex;

                    return cellRefErrorToken;
                }

                return {
                    ...formulaToken,
                    token: {
                        ...formulaToken.token,
                        range: newRange
                    } as IFormulaToken
                } as ILinearFormulaToken;
            }

            formulaToken.token.startIndex = formulaToken.token.startIndex + tokenPositionOffset;
            formulaToken.token.endIndex = formulaToken.token.endIndex + tokenPositionOffset;

            return formulaToken;
        });
    }

    public recalculateCellReferencesForPastingCells(formulaTokens:Array<ILinearFormulaToken>,
                                                    fromSheetId:SheetId,
                                                    slotLocation:SlotLocation,
                                                    toSheetId:SheetId,
                                                    copiedRange:MeshRange,
                                                    toColumnIndex:number,
                                                    toRowIndex:number):Array<ILinearFormulaToken>
    {
        return formulaTokens && formulaTokens.map((formulaToken:ILinearFormulaToken):ILinearFormulaToken =>
        {
            if( formulaToken.token.type === FormulaInputTokenType.CellReference )
            {
                const location:R1C1Location = (formulaToken.token as ICellReferenceFormulaToken).location;

                if( !this._isLocationInCopiedRange(location, slotLocation, copiedRange) )
                {
                    const newLocation = this._recalculateR1C1LocationForPasting(
                        location,
                        toColumnIndex - copiedRange.from.colIndex,
                        toRowIndex - copiedRange.from.rowIndex
                    );

                    const shouldChangeType:boolean = fromSheetId !== toSheetId;
                    let newProperties:object = {};

                    if( shouldChangeType )
                    {
                        const sheet:AnySheet = commonControllers.spreadsheets.main.findAnySheetById(fromSheetId);

                        if( sheet )
                        {
                            const sheetNamePrepared = `'${sheet.name.replace(/'/g, '\'\'')}'`;

                            newProperties = {
                                type: FormulaInputTokenType.CellReferenceExternal,
                                sheetName: sheetNamePrepared
                            };
                        }
                    }

                    return {
                        ...formulaToken,
                        token: {
                            ...formulaToken.token,
                            location: newLocation,
                            range: new R1C1Range(newLocation, newLocation),
                            ...newProperties
                        } as IFormulaToken
                    } as ILinearFormulaToken;
                }
            }
            else if( formulaToken.token.type === FormulaInputTokenType.CellReferenceExternal )
            {
                const {location, sheetName} = (formulaToken.token as ICellReferenceExternalFormulaToken);

                const sheetWhereSlotWas:AnySheet = commonControllers.spreadsheets.main.findAnySheetById(fromSheetId);
                const sheetNameToFindSheet:string = MeshUtils.refSheetNameToSheetName(sheetName);
                const formulaTokenSheet:AnySheet = commonControllers.spreadsheets.workbook.findSheetByName(
                    sheetWhereSlotWas.workbook.id,
                    sheetNameToFindSheet
                );

                if( formulaTokenSheet &&
                    !(formulaTokenSheet.id === fromSheetId && this._isLocationInCopiedRange(location, slotLocation, copiedRange))
                )
                {
                    const newLocation = this._recalculateR1C1LocationForPasting(
                        location,
                        toColumnIndex - copiedRange.from.colIndex,
                        toRowIndex - copiedRange.from.rowIndex
                    );

                    return {
                        ...formulaToken,
                        token: {
                            ...formulaToken.token,
                            location: newLocation,
                            range: new R1C1Range(newLocation, newLocation)
                        } as IFormulaToken
                    } as ILinearFormulaToken;
                }
            }
            else if( formulaToken.token.type === FormulaInputTokenType.CellReferenceRange )
            {
                const {startLocation, endLocation} = (formulaToken.token as ICellReferenceRangeFormulaToken).range;

                if( !this._isCopiedRangeIncludesAllTokenRange(startLocation, endLocation, slotLocation, copiedRange) )
                {
                    const newStartLocation = this._recalculateR1C1LocationForPasting(
                        startLocation,
                        toColumnIndex - copiedRange.from.colIndex,
                        toRowIndex - copiedRange.from.rowIndex
                    );

                    const newEndLocation = this._recalculateR1C1LocationForPasting(
                        endLocation,
                        toColumnIndex - copiedRange.from.colIndex,
                        toRowIndex - copiedRange.from.rowIndex
                    );

                    const shouldChangeType:boolean = fromSheetId !== toSheetId;
                    let newProperties:object = {};

                    if( shouldChangeType )
                    {
                        const sheet:AnySheet = commonControllers.spreadsheets.main.findAnySheetById(fromSheetId);

                        if( sheet )
                        {
                            const sheetNamePrepared = `'${sheet.name.replace(/'/g, '\'\'')}'`;

                            newProperties = {
                                type: FormulaInputTokenType.CellReferenceRangeExternal,
                                sheetName: sheetNamePrepared
                            };
                        }
                    }

                    return {
                        ...formulaToken,
                        token: {
                            ...formulaToken.token,
                            range: new R1C1Range(
                                newStartLocation,
                                newEndLocation
                            ),
                            ...newProperties
                        } as IFormulaToken
                    } as ILinearFormulaToken;
                }
            }
            else if( formulaToken.token.type === FormulaInputTokenType.CellReferenceRangeExternal )
            {
                const {
                    range: {
                        startLocation,
                        endLocation
                    },
                    sheetName
                } = (formulaToken.token as ICellReferenceRangeExternalFormulaToken);

                const sheetWhereSlotWas:AnySheet = commonControllers.spreadsheets.main.findAnySheetById(fromSheetId);
                const sheetNameToFindSheet:string = MeshUtils.refSheetNameToSheetName(sheetName);
                const formulaTokenSheet:AnySheet = commonControllers.spreadsheets.workbook.findSheetByName(
                    sheetWhereSlotWas.workbook.id,
                    sheetNameToFindSheet
                );

                if( formulaTokenSheet && !(formulaTokenSheet.id === fromSheetId &&
                    this._isCopiedRangeIncludesAllTokenRange(startLocation, endLocation, slotLocation, copiedRange)) )
                {
                    const newStartLocation = this._recalculateR1C1LocationForPasting(
                        startLocation,
                        toColumnIndex - copiedRange.from.colIndex,
                        toRowIndex - copiedRange.from.rowIndex
                    );

                    const newEndLocation = this._recalculateR1C1LocationForPasting(
                        endLocation,
                        toColumnIndex - copiedRange.from.colIndex,
                        toRowIndex - copiedRange.from.rowIndex
                    );

                    return {
                        ...formulaToken,
                        token: {
                            ...formulaToken.token,
                            range: new R1C1Range(
                                newStartLocation,
                                newEndLocation
                            )
                        } as IFormulaToken
                    } as ILinearFormulaToken;
                }
            }

            return formulaToken;
        });
    }

    private del_parseSheetName(sheetName:string)
    {
        if( sheetName.startsWith('\'') && sheetName.endsWith('\'') )
            return sheetName.slice(1, -1).replace(/''/g, '\'');

        return sheetName;
    }

    private _recalculateR1C1LocationForPasting(location:R1C1Location,
                                               deltaColumns:number,
                                               deltaRows:number,
                                               isForSlotsWithRefsToCut:boolean = false):R1C1Location
    {
        let newColumnOffset:number = location.columnOffset;
        let newRowOffset:number = location.rowOffset;

        if( deltaColumns !== 0 && location.columnOffset !== null )
        {
            if( isForSlotsWithRefsToCut )
                newColumnOffset += deltaColumns;
            else if( !location.isColumnFixed )
                newColumnOffset -= deltaColumns;
        }

        if( deltaRows !== 0 && location.rowOffset !== null )
        {
            if( isForSlotsWithRefsToCut )
                newRowOffset += deltaRows;
            else if( !location.isRowFixed )
                newRowOffset -= deltaRows;
        }

        return new R1C1Location(
            newRowOffset,
            location.isRowFixed,
            newColumnOffset,
            location.isColumnFixed
        );
    }

    private _isLocationInCopiedRange(location:R1C1Location, slotLocation:SlotLocation, copiedRange:MeshRange):boolean
    {
        const {colIndex, rowIndex}:R1C1LocationIndexes = location.getIndexes(slotLocation.colIndex, slotLocation.rowIndex);
        const {
            from: {
                colIndex: fromColIndex,
                rowIndex: fromRowIndex
            },
            to: {
                colIndex: toColIndex,
                rowIndex: toRowIndex
            }
        }:MeshRange = copiedRange;

        return (colIndex >= fromColIndex && colIndex <= toColIndex)
            && (rowIndex >= fromRowIndex && rowIndex <= toRowIndex);
    }

    private _isCopiedRangeIncludesAllTokenRange(startLocation:R1C1Location,
                                                endLocation:R1C1Location,
                                                slotLocation:SlotLocation,
                                                copiedRange:MeshRange):boolean
    {
        return this._isLocationInCopiedRange(startLocation, slotLocation, copiedRange) &&
            this._isLocationInCopiedRange(endLocation, slotLocation, copiedRange);
    }

    public recalculateCellReferencesForSlotsWithRefsToCutCells(formulaTokens:Array<ILinearFormulaToken>,
                                                               slotSheetId:SheetId,
                                                               slotLocation:SlotLocation,
                                                               fromSheetId:SheetId,
                                                               toSheetId:SheetId,
                                                               copiedRange:MeshRange,
                                                               toColumnIndex:number,
                                                               toRowIndex:number):Array<ILinearFormulaToken>
    {
        return formulaTokens && formulaTokens.map((formulaToken:ILinearFormulaToken):ILinearFormulaToken =>
        {
            if( formulaToken.token.type === FormulaInputTokenType.CellReference )
            {
                const location:R1C1Location = (formulaToken.token as ICellReferenceFormulaToken).location;

                if( this._isLocationInCopiedRange(location, slotLocation, copiedRange) )
                {
                    const newLocation = this._recalculateR1C1LocationForPasting(
                        location,
                        toColumnIndex - copiedRange.from.colIndex,
                        toRowIndex - copiedRange.from.rowIndex,
                        true
                    );

                    const shouldChangeType:boolean = fromSheetId !== toSheetId;
                    let newProperties:object = {};

                    if( shouldChangeType )
                    {
                        const sheet:AnySheet = commonControllers.spreadsheets.main.findAnySheetById(toSheetId);

                        if( sheet )
                        {
                            const sheetNamePrepared = `'${sheet.name.replace(/'/g, '\'\'')}'`;

                            newProperties = {
                                type: FormulaInputTokenType.CellReferenceExternal,
                                sheetName: sheetNamePrepared
                            };
                        }
                    }

                    return {
                        ...formulaToken,
                        token: {
                            ...formulaToken.token,
                            location: newLocation,
                            range: new R1C1Range(newLocation, newLocation),
                            ...newProperties
                        } as IFormulaToken
                    } as ILinearFormulaToken;
                }
            }
            else if( formulaToken.token.type === FormulaInputTokenType.CellReferenceExternal )
            {
                const {location, sheetName} = (formulaToken.token as ICellReferenceExternalFormulaToken);

                const sheetWhereSlotIs:AnySheet = commonControllers.spreadsheets.main.findAnySheetById(slotSheetId);
                const sheetNameToFindSheet:string = MeshUtils.refSheetNameToSheetName(sheetName);
                const formulaTokenSheet:AnySheet = commonControllers.spreadsheets.workbook.findSheetByName(
                    sheetWhereSlotIs.workbook.id,
                    sheetNameToFindSheet
                );

                if( formulaTokenSheet.id === fromSheetId && this._isLocationInCopiedRange(location, slotLocation, copiedRange) )
                {
                    const newLocation = this._recalculateR1C1LocationForPasting(
                        location,
                        toColumnIndex - copiedRange.from.colIndex,
                        toRowIndex - copiedRange.from.rowIndex,
                        true
                    );

                    let sheetNameForToken:string = sheetName;

                    if( fromSheetId !== toSheetId )
                    {
                        const sheet:AnySheet = commonControllers.spreadsheets.main.findAnySheetById(toSheetId);

                        if( sheet )
                            sheetNameForToken = `'${sheet.name.replace(/'/g, '\'\'')}'`;
                    }

                    return {
                        ...formulaToken,
                        token: {
                            ...formulaToken.token,
                            location: newLocation,
                            range: new R1C1Range(newLocation, newLocation),
                            sheetName: sheetNameForToken
                        } as IFormulaToken
                    } as ILinearFormulaToken;
                }
            }
            else if( formulaToken.token.type === FormulaInputTokenType.CellReferenceRange )
            {
                const {startLocation, endLocation} = (formulaToken.token as ICellReferenceRangeFormulaToken).range;

                if( this._isCopiedRangeIncludesAllTokenRange(startLocation, endLocation, slotLocation, copiedRange) )
                {
                    const newStartLocation = this._recalculateR1C1LocationForPasting(
                        startLocation,
                        toColumnIndex - copiedRange.from.colIndex,
                        toRowIndex - copiedRange.from.rowIndex,
                        true
                    );

                    const newEndLocation = this._recalculateR1C1LocationForPasting(
                        endLocation,
                        toColumnIndex - copiedRange.from.colIndex,
                        toRowIndex - copiedRange.from.rowIndex,
                        true
                    );

                    const shouldChangeType:boolean = fromSheetId !== toSheetId;
                    let newProperties:object = {};

                    if( shouldChangeType )
                    {
                        const sheet:AnySheet = commonControllers.spreadsheets.main.findAnySheetById(toSheetId);

                        if( sheet )
                        {
                            const sheetNamePrepared = `'${sheet.name.replace(/'/g, '\'\'')}'`;

                            newProperties = {
                                type: FormulaInputTokenType.CellReferenceRangeExternal,
                                sheetName: sheetNamePrepared
                            };
                        }
                    }

                    return {
                        ...formulaToken,
                        token: {
                            ...formulaToken.token,
                            range: new R1C1Range(
                                newStartLocation,
                                newEndLocation
                            ),
                            ...newProperties
                        } as IFormulaToken
                    } as ILinearFormulaToken;
                }
            }
            else if( formulaToken.token.type === FormulaInputTokenType.CellReferenceRangeExternal )
            {
                const {
                    range: {
                        startLocation,
                        endLocation
                    },
                    sheetName
                } = (formulaToken.token as ICellReferenceRangeExternalFormulaToken);

                const sheetWhereSlotIs:AnySheet = commonControllers.spreadsheets.main.findAnySheetById(slotSheetId);
                const sheetNameToFindSheet:string = MeshUtils.refSheetNameToSheetName(sheetName);
                const formulaTokenSheet:AnySheet = commonControllers.spreadsheets.workbook.findSheetByName(
                    sheetWhereSlotIs.workbook.id,
                    sheetNameToFindSheet
                );

                if( formulaTokenSheet.id === fromSheetId &&
                    this._isCopiedRangeIncludesAllTokenRange(startLocation, endLocation, slotLocation, copiedRange) )
                {
                    const newStartLocation = this._recalculateR1C1LocationForPasting(
                        startLocation,
                        toColumnIndex - copiedRange.from.colIndex,
                        toRowIndex - copiedRange.from.rowIndex,
                        true
                    );

                    const newEndLocation = this._recalculateR1C1LocationForPasting(
                        endLocation,
                        toColumnIndex - copiedRange.from.colIndex,
                        toRowIndex - copiedRange.from.rowIndex,
                        true
                    );

                    let sheetNameForToken:string = sheetName;

                    if( fromSheetId !== toSheetId )
                    {
                        const sheet:AnySheet = commonControllers.spreadsheets.main.findAnySheetById(toSheetId);

                        if( sheet )
                            sheetNameForToken = `'${sheet.name.replace(/'/g, '\'\'')}'`;
                    }

                    return {
                        ...formulaToken,
                        token: {
                            ...formulaToken.token,
                            range: new R1C1Range(
                                newStartLocation,
                                newEndLocation
                            ),
                            sheetName: sheetNameForToken
                        } as IFormulaToken
                    } as ILinearFormulaToken;
                }
            }

            return formulaToken;
        });
    }

    public normalizeCellReferences(formulaTokens:Array<ILinearFormulaToken>,
                                   currentColumnIndex:number,
                                   currentRowIndex:number):Array<ILinearFormulaToken> // TODO: Finish normalization
    {
        let tokenPositionOffset = 0;

        const hasTokenCellRefError = (location:R1C1Location):boolean =>
        {
            if( location == null )
                return true;

            if( location.columnOffset != null )
            {
                const resultColumnIndex:number = location.isColumnFixed
                    ? location.columnOffset
                    : currentColumnIndex + location.columnOffset + 1;

                if( resultColumnIndex < 1 )
                {
                    return true;
                }
            }

            if( location.rowOffset != null )
            {
                const resultRowIndex:number = location.isRowFixed
                    ? location.rowOffset
                    : currentRowIndex + location.rowOffset + 1;

                if( resultRowIndex < 1 )
                {
                    return true;
                }
            }

            return false;
        };

        return formulaTokens && formulaTokens.map((formulaToken:ILinearFormulaToken):ILinearFormulaToken =>
        {
            if( formulaToken.token.type === FormulaInputTokenType.CellReference )
            {
                const location:R1C1Location = (formulaToken.token as ICellReferenceFormulaToken).location;

                if( hasTokenCellRefError(location) )
                {
                    return this._getCellReferenceErrorToken(formulaToken);
                }

                formulaToken.token.value = location.toA1String(currentColumnIndex, currentRowIndex);
            }
            else if( formulaToken.token.type === FormulaInputTokenType.CellReferenceRange )
            {
                const {startLocation, endLocation} = (formulaToken.token as ICellReferenceRangeFormulaToken).range;

                if( hasTokenCellRefError(startLocation) || (endLocation && hasTokenCellRefError(endLocation)) )
                {
                    return this._getCellReferenceErrorToken(formulaToken);
                }

                const startLocationA1 = startLocation.toA1String(currentColumnIndex, currentRowIndex);

                if( endLocation )
                {
                    const endLocationA1 = endLocation.toA1String(currentColumnIndex, currentRowIndex);
                    formulaToken.token.value = `${startLocationA1}:${endLocationA1}`;
                }
                else
                {
                    formulaToken.token.value = `${startLocationA1}:`;
                }
            }

            formulaToken.token.startIndex = formulaToken.token.startIndex + tokenPositionOffset;
            formulaToken.token.endIndex = formulaToken.token.endIndex + tokenPositionOffset;

            return formulaToken;
        });
    }

    public replaceSheetNameInExternalCellRefs(formulaTokens:Array<ILinearFormulaToken>,
                                              oldSheetName:string,
                                              newSheetName:string):Array<ILinearFormulaToken>
    {
        newSheetName = MeshUtils.sheetNameToRefSheetName(newSheetName);

        return formulaTokens && formulaTokens.map((formulaToken:ILinearFormulaToken):ILinearFormulaToken =>
        {
            if( formulaToken.token.type === FormulaInputTokenType.CellReferenceExternal
                || formulaToken.token.type === FormulaInputTokenType.CellReferenceRangeExternal )
            {
                const cellRefToken = formulaToken.token as ICellReferenceExternalFormulaToken;
                const currentSheetName = MeshUtils.refSheetNameToSheetName(cellRefToken.sheetName);

                if( currentSheetName === oldSheetName )
                    cellRefToken.sheetName = newSheetName;
            }

            return formulaToken;
        });
    }
}
